/**
 * Formulaire de création de campagne, partagé entre le portail client et le
 * portail admin (§ « le portail admin et le portail client utilisent le même
 * formulaire de création de campagne »).
 *
 * Les deux portails n'ont pas la même autorité sur l'organisation ciblée : le
 * portail client agit toujours pour l'utilisateur connecté (son org et son
 * groupe sont résolus côté serveur, cf. `campagnes.creerCampagne` /
 * `getOrgScopedUser`), le portail admin cible une organisation explicite en
 * tant que super-admin (`superAdmin.creerCampagneSuperAdmin`). C'est la seule
 * différence de fond : `organisationId` présent bascule le formulaire en
 * mode admin (mutation + sélecteur de groupe + candidats masqués, ajoutés
 * ensuite depuis le portail client).
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@repo/ui";
import { useMutation, useQuery } from "convex/react";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Info,
  Loader2,
} from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { ApplicationEnum } from "@repo/utils";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  CandidatsTableSection,
  type CandidatRow,
} from "./CandidatsTableSection";
import { ChoixEpreuves } from "./ChoixEpreuves";
import {
  CHOIX_INITIAL,
  erreurComposition,
  evaluationsDeComposition,
  type ChoixComposition,
} from "./compositionCampagne";

/** Valeur du sélecteur de groupe pour « aucun groupe » (Radix refuse ""). */
const SANS_GROUPE = "__aucun__";

export interface CampagneCreatorFormProps {
  /**
   * Présent → mode admin : mutation `superAdmin.creerCampagneSuperAdmin`,
   * groupe sélectionnable, tableau de candidats masqué. Absent → mode
   * client : mutation `campagnes.creerCampagne`, org/groupe résolus côté
   * serveur depuis l'utilisateur connecté.
   */
  organisationId?: Id<"organisations">;
  /** Personnalise l'intro du formulaire (mode admin). */
  organisationNom?: string;
  /**
   * Applications proposables (org, ou groupe de l'utilisateur) ; `undefined`
   * pendant le chargement. Le serveur revalide la composition.
   */
  applicationsAutorisees: readonly ApplicationEnum[] | undefined;
  /**
   * Sélecteur de groupe cible (mode admin uniquement). Un groupe qui
   * restreint les applications borne les cases proposées.
   */
  groupes?: {
    _id: Id<"groups">;
    nom: string;
    applicationsAutorisees?: ApplicationEnum[];
  }[];
  /** Mode admin : les candidats s'ajoutent après coup depuis le portail client. */
  includeCandidats?: boolean;
  retourHref: string;
  retourLabel: string;
  getCampagneHref: (campagneId: string) => string;
  /** Base de l'URL candidat (portail libre-service) ; omise si non configurée pour ce portail. */
  libreServiceBaseUrl?: string;
}

export function CampagneCreatorForm({
  organisationId,
  organisationNom,
  applicationsAutorisees,
  groupes,
  includeCandidats = true,
  retourHref,
  retourLabel,
  getCampagneHref,
  libreServiceBaseUrl,
}: CampagneCreatorFormProps) {
  const creerCampagneClient = useMutation(api.campagnes.creerCampagne);
  const creerCampagneAdmin = useMutation(api.superAdmin.creerCampagneSuperAdmin);
  const scorecards = useQuery(
    api.scorecards.listerScorecardsPourCampagne,
    organisationId ? { organisationId } : {},
  );

  const [nom, setNom] = useState("");
  const [groupId, setGroupId] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [choix, setChoix] = useState<ChoixComposition>(CHOIX_INITIAL);
  const [datesOpen, setDatesOpen] = useState(false);
  const [candidats, setCandidats] = useState<CandidatRow[]>([]);
  const [anonyme, setAnonyme] = useState(false);
  const [autoriserCreation, setAutoriserCreation] = useState(false);
  const [motDePasseCampagne, setMotDePasseCampagne] = useState("");
  const [motDePasseCampagneConfirm, setMotDePasseCampagneConfirm] =
    useState("");
  const [showMotDePasse, setShowMotDePasse] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    campagneId: string;
    nbParcours: number;
    libreService?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const succesRef = useRef<HTMLDivElement>(null);

  // La carte de succès est rendue au-dessus d'un formulaire long : sans ce
  // scroll elle reste hors écran, l'utilisateur croit que rien ne s'est passé
  // et re-soumet. C'était la cause des campagnes créées en double.
  useEffect(() => {
    if (result) {
      succesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  /** Remet le formulaire à son état initial pour une création suivante. */
  const reinitialiserFormulaire = () => {
    setResult(null);
    setError(null);
    setNom("");
    setGroupId("");
    setDateDebut("");
    setDateFin("");
    setDatesOpen(false);
    setCandidats([]);
    setAnonyme(false);
    setAutoriserCreation(false);
    setMotDePasseCampagne("");
    setMotDePasseCampagneConfirm("");
    setShowMotDePasse(false);
    setChoix(CHOIX_INITIAL);
  };

  // Le groupe ciblé peut restreindre les applications de l'organisation ; une
  // case cochée qui n'est plus proposée ne part pas.
  const restrictionGroupe = groupes?.find((g) => g._id === groupId)
    ?.applicationsAutorisees;
  const autorisees = restrictionGroupe ?? applicationsAutorisees ?? [];
  const choixEffectif: ChoixComposition = {
    ...choix,
    cochees: choix.cochees.filter((c) => autorisees.includes(c)),
  };

  const genererToken = (index: number) => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    const part = (n: number) =>
      Array.from(
        { length: n },
        () => chars[Math.floor(Math.random() * chars.length)],
      ).join("");
    const newToken = `${part(4)}-${part(4)}`;
    setCandidats((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current) updated[index] = { ...current, token: newToken };
      return updated;
    });
  };

  const addCandidatRow = () => {
    setCandidats((prev) => [
      ...prev,
      { emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" },
    ]);
  };

  const removeCandidatRow = (index: number) => {
    setCandidats((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCandidat = (
    index: number,
    field: keyof CandidatRow,
    value: string,
  ) => {
    setCandidats((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current) updated[index] = { ...current, [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const erreur = erreurComposition(choixEffectif);
      if (erreur) throw new Error(erreur);

      if (
        autoriserCreation &&
        motDePasseCampagne !== motDePasseCampagneConfirm
      ) {
        throw new Error("Les mots de passe ne correspondent pas.");
      }

      const validCandidats = includeCandidats
        ? candidats.filter((c) =>
            anonyme
              ? c.token
              : c.emailCandidat && c.nomCandidat && c.prenomCandidat,
          )
        : [];

      const commonArgs = {
        nom: nom || undefined,
        evaluationsConfig: evaluationsDeComposition(choixEffectif),
        dateDebut: dateDebut ? new Date(dateDebut).getTime() : undefined,
        dateFin: dateFin ? new Date(dateFin).getTime() : undefined,
        anonyme,
        autoriserCreationParcoursCandidats: autoriserCreation || undefined,
        motDePasseCampagne:
          autoriserCreation && motDePasseCampagne
            ? motDePasseCampagne
            : undefined,
        candidats:
          validCandidats.length > 0
            ? validCandidats.map((c) => ({
                emailCandidat: c.emailCandidat,
                nomCandidat: anonyme ? c.token : c.nomCandidat,
                prenomCandidat: c.prenomCandidat,
                token: c.token || undefined,
              }))
            : undefined,
      };

      const groupIdCible = groupId ? (groupId as Id<"groups">) : undefined;

      const response = organisationId
        ? await creerCampagneAdmin({
            ...commonArgs,
            organisationId,
            groupId: groupIdCible,
          })
        : await creerCampagneClient({ ...commonArgs, groupId: groupIdCible });

      setResult({
        campagneId: response.campagneId,
        nbParcours: response.parcoursCrees.length,
        libreService:
          autoriserCreation && libreServiceBaseUrl
            ? `${libreServiceBaseUrl}/c/${response.campagneId}/new`
            : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid gap-6">
      <div className="mb-0">
        <Link
          to={retourHref}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← {retourLabel}
        </Link>
      </div>

      {result && (
        <Card ref={succesRef} className="border-etat-fait">
          <CardHeader>
            <CardTitle className="text-etat-fait">
              Campagne créée avec succès !
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 font-mono text-sm">
              <div>
                <strong>Campagne ID :</strong> {result.campagneId}
              </div>
              <div>
                <strong>Parcours créés :</strong> {result.nbParcours}
              </div>
              {result.libreService && (
                <div className="mt-2 rounded-surface border bg-muted p-3">
                  <p className="text-xs font-sans text-muted-foreground mb-1">
                    Lien candidat libre-service :
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs break-all flex-1">
                      {result.libreService}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(result.libreService!);
                      }}
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                      aria-label="Copier le lien"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              <div className="mt-4 flex gap-2 font-sans">
                <Link to={getCampagneHref(result.campagneId)}>
                  <Button variant="outline">Voir la campagne</Button>
                </Link>
                <Link to={retourHref}>
                  <Button variant="outline">{retourLabel}</Button>
                </Link>
                <Button
                  type="button"
                  variant="outline"
                  onClick={reinitialiserFormulaire}
                >
                  Créer une autre campagne
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-etat-erreur">
          <CardHeader>
            <CardTitle className="text-etat-erreur">Erreur</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-sm text-etat-erreur whitespace-pre-wrap">
              {error}
            </pre>
          </CardContent>
        </Card>
      )}

      {!result && (
      <Card className="min-w-112.5">
        <CardHeader>
          <CardTitle>Créer une campagne</CardTitle>
          <CardDescription>
            {organisationNom
              ? `Configurez la campagne pour ${organisationNom}.`
              : "Configurez la campagne."}{" "}
            {includeCandidats
              ? "Les candidats peuvent être ajoutés maintenant ou plus tard."
              : "Les candidats s'ajoutent ensuite depuis le portail client."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-6">
            {applicationsAutorisees === undefined ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <ChoixEpreuves
                autorisees={autorisees}
                choix={choixEffectif}
                onChange={setChoix}
                scorecards={scorecards}
              />
            )}

            {groupes && groupes.length > 0 && (
              <div className="grid gap-2">
                <Label htmlFor="groupe">Groupe (optionnel)</Label>
                {/* Radix interdit une SelectItem de valeur vide : l'absence de
                    groupe passe par la sentinelle SANS_GROUPE, retraduite en
                    `undefined` à l'envoi. */}
                <Select
                  value={groupId || SANS_GROUPE}
                  onValueChange={(v) => setGroupId(v === SANS_GROUPE ? "" : v)}
                >
                  <SelectTrigger id="groupe">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SANS_GROUPE}>
                      Sans groupe (toute l&apos;organisation)
                    </SelectItem>
                    {groupes.map((g) => (
                      <SelectItem key={g._id} value={g._id}>
                        {g.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Sans groupe, la campagne est visible par toute
                  l&apos;organisation. En choisir un la réserve à ses membres.
                </p>
              </div>
            )}

            <div className="space-y-3 rounded-surface border p-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="anonyme"
                  checked={anonyme}
                  onCheckedChange={(coche) => setAnonyme(coche === true)}
                />
                <Label htmlFor="anonyme">Campagne anonyme</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="En savoir plus sur le mode anonyme"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72">
                    Les candidats ne seront pas identifiés par leur nom :
                    chacun recevra un code d&apos;accès personnel à la
                    place. Leur identité restera masquée partout dans
                    l&apos;application (résultats, tableaux de bord). Ce
                    choix ne peut plus être modifié une fois la campagne
                    créée.
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="autoriserCreation"
                  checked={autoriserCreation}
                  onCheckedChange={(coche) => {
                    const actif = coche === true;
                    setAutoriserCreation(actif);
                    if (!actif) {
                      setMotDePasseCampagne("");
                      setMotDePasseCampagneConfirm("");
                    }
                  }}
                />
                <Label htmlFor="autoriserCreation">
                  Autoriser la création de parcours par les candidats
                  (nécessite la création d&apos;un mot de passe)
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="En savoir plus sur la création de parcours par les candidats"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72">
                    Un lien sera généré, à communiquer aux candidats : en
                    s&apos;y rendant, chacun pourra créer lui-même son
                    parcours, sans que vous ayez à l&apos;ajouter
                    manuellement. Un mot de passe deviendra obligatoire pour
                    protéger l&apos;accès à ce lien.
                  </TooltipContent>
                </Tooltip>
              </div>

              {autoriserCreation && (
                <>
                  <div className="grid gap-2 pl-6">
                    <Label htmlFor="motDePasseCampagne">
                      Mot de passe d'accès{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="motDePasseCampagne"
                        type={showMotDePasse ? "text" : "password"}
                        value={motDePasseCampagne}
                        onChange={(e) => setMotDePasseCampagne(e.target.value)}
                        placeholder="Mot de passe à communiquer aux candidats"
                        required={autoriserCreation}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowMotDePasse((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showMotDePasse ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-2 pl-6">
                    <Label htmlFor="motDePasseCampagneConfirm">
                      Confirmer le mot de passe{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="motDePasseCampagneConfirm"
                        type={showMotDePasse ? "text" : "password"}
                        value={motDePasseCampagneConfirm}
                        onChange={(e) =>
                          setMotDePasseCampagneConfirm(e.target.value)
                        }
                        placeholder="Répéter le mot de passe"
                        required={autoriserCreation}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowMotDePasse((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showMotDePasse ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="nom">Nom de la campagne (optionnel)</Label>
              <Input
                id="nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Auto-généré : épreuves - date"
              />
            </div>

            <div className="rounded-surface border">
              <button
                type="button"
                onClick={() => setDatesOpen((v) => !v)}
                className="flex w-full items-center justify-between p-4 text-sm font-medium"
                aria-expanded={datesOpen}
              >
                <span>Dates de la campagne (optionnel)</span>
                {datesOpen ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
              {datesOpen && (
                <div className="grid grid-cols-2 gap-4 p-4 pt-0">
                  <div className="grid gap-2">
                    <Label htmlFor="dateDebut">Date de début</Label>
                    <Input
                      id="dateDebut"
                      type="date"
                      value={dateDebut}
                      onChange={(e) => setDateDebut(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="dateFin">Date de fin</Label>
                    <Input
                      id="dateFin"
                      type="date"
                      value={dateFin}
                      onChange={(e) => setDateFin(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {includeCandidats && (
              <CandidatsTableSection
                candidats={candidats}
                anonyme={anonyme}
                onUpdate={updateCandidat}
                onRemove={removeCandidatRow}
                onAdd={addCandidatRow}
                onGenererToken={genererToken}
              />
            )}

            <div className="flex gap-2">
              <Button type="submit" disabled={isLoading} className="mt-2">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  "Créer la campagne"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
